import { stopSubmit } from 'redux-form';
import { normalize, schema } from 'normalizr';
import moment from 'moment';
import Config from 'Config';
import globalize, { i18n } from '../services/globalize';
import { refreshToken } from '../../auth/actions';

export const API_ROOT = Config.frontofficeApiUrl;

export const getDefaultRequestHeaders = state => ({
    'Content-Type': 'application/json',
    'X-Franchisor-Id': state.configuration.franchisor.id,
    'Accept-Language': globalize.getCurrentCulture(state.configuration.franchisor)
});

const callApi = (apiOptions, schema, dispatch, getState, isRetry) => {
    const state = getState();

    const { method, endpoint, body, authorize } = apiOptions;

    const fullUrl = endpoint.indexOf(API_ROOT) === -1 ? `${API_ROOT}/${endpoint}` : endpoint;

    const options = {
        method,
        headers: getDefaultRequestHeaders(state),
		body
    };

    if (authorize) {
        options.headers['Authorization'] = `Bearer ${state.auth.accessToken}`;
    }

    return fetch(fullUrl, options)
        .then(async response => {
            let json;
            try {
                json = await response.json();
            } catch (jsonError) { }

            if (!response.ok) {
                if (response.status === 401 && !isRetry) {
                    // refresh access token and retry current API call
                    return dispatch(refreshToken())
                        .then(() => callApi(apiOptions, schema, dispatch, getState, true))
                        .catch((refreshError) => Promise.reject(refreshError));
                }
                else {
                    return Promise.reject(json);
                }
            }

            if (schema) {
                const { data, totalCount } = json;
                if (data !== undefined && totalCount !== undefined) {
                    return Object.assign({},
                        normalize(data, schema),
                        { totalCount }
                    );
                }

                return normalize(json, schema);
            }

            return json;
        });
};

const encodeQueryParams = (params) => {
    let queryString = '';

    if (params) {
        queryString = Object.keys(params)
            .flatMap(k => {
                const paramValue = params[k];
                let values = [paramValue];
                if (Array.isArray(paramValue)) {
                    values = [...paramValue];
                }
                return values.map(value => {
                    if (value instanceof Date) {
                        value = moment(value).format();
                    }

                    return value ? encodeURIComponent(k) + '=' + encodeURIComponent(value) : null;
                });
            })
            .filter(queryParam => queryParam !== null && queryParam !== undefined)
            .join('&');
    }

    return queryString;
};

export const processErrorResponse = (errorResponse) => {
    if (typeof errorResponse === 'string') {
        return {
            message: errorResponse
        };
    }

    const { title, detail, errors } = errorResponse;
    const validationErrors = {};

    if (errors) {
        Object.keys(errors).forEach(key => {
            const dotPosition = key.lastIndexOf('.');
            let propName = key.substring(dotPosition + 1);
            propName = propName.charAt(0).toLowerCase() + propName.slice(1);

            validationErrors[propName] = errors[key][0];
        });
    }

    const newMessage = detail || (title === "One or more validation errors occurred." ? i18n.t('BadRequest_Message') : title);

    return {
        message: newMessage,
        validationErrors
    };
};

const centerSchema = new schema.Entity('centers', {}, {
    idAttribute: center => center.id
});

const individualConsultationScheduleSchema = new schema.Entity('individualConsultationSchedule', {}, {
    idAttribute: item => `${item.centerId}:${item.consultationType}@${item.start}`
});

const weeklyProgramSchema = new schema.Entity('weeklyProgram', {}, {
    idAttribute: item => item.id
});

const lessonSchema = new schema.Entity('lessons', {}, {
    idAttribute: lesson => `${lesson.programId}@${lesson.start}`
});

const gradeSchema = new schema.Entity('grades', {}, {
    idAttribute: grade => grade.id
});

const termSchema = new schema.Entity('terms', {}, {
    idAttribute: term => term.id
});

const paymentSchema = new schema.Entity('payments', {}, {
    idAttribute: payment => payment.id
});

const enrolmentSchema = new schema.Entity('enrolments', {}, {
    idAttribute: enrolment => enrolment.id
});

const individualConsultationSchema = new schema.Entity('individualConsultations', {}, {
    idAttribute: individualConsultation => individualConsultation.id
});

const individualConsultationDashboardSchema = new schema.Entity('individualConsultationsDashboard', {}, {
    idAttribute: individualConsultation => individualConsultation.id
});

const workshopSessionsDashboardSchema = new schema.Entity('workshopSessionsDashboard', {}, {
    idAttribute: session => session.id
});

const individualConsultationDocumentSchema = new schema.Entity('individualConsultationDocuments', {}, {
    idAttribute: individualConsultationDocument => individualConsultationDocument.id
});

const childLessonSchema = new schema.Entity('childLessons', {}, {
    idAttribute: childLesson => childLesson.id
});

const weeklyInfoSchema = new schema.Entity('weeklyInfos', {}, {
    idAttribute: weeklyInfo => `${weeklyInfo.gradeId}|${weeklyInfo.weekNumber}`
});

const selfAssessmentSchema = new schema.Entity('selfAssessments', {}, {
    idAttribute: selfAssessment => `${selfAssessment.childId}|${selfAssessment.formId}`
});

const medicalInformationSchema = new schema.Entity('medicalInformations', {}, {
    idAttribute: medicalInfoFormResponse => medicalInfoFormResponse.childId
});

const workshopSchema = new schema.Entity('workshops', {}, {
    idAttribute: workshop => workshop.id
});

const workshopSessionSchema = new schema.Entity('workshopSessions', {}, {
    idAttribute: session => session.id
});

const workshopEnrolmentSchema = new schema.Entity('workshopEnrolments', {}, {
    idAttribute: workshopEnrolment => workshopEnrolment.id
});

const makeupSummarySchema = new schema.Entity('makeupSummary', {}, {
    idAttribute: childLesson => childLesson.id
});

export const Schemas = {
    CENTER: centerSchema,
    CENTER_ARRAY: [centerSchema],
    INDIVIDUAL_CONSULTATION_SCHEDULE: individualConsultationScheduleSchema,
    INDIVIDUAL_CONSULTATION_SCHEDULE_ARRAY: [individualConsultationScheduleSchema],
    WEEKLY_PROGRAM_ITEM: weeklyProgramSchema,
    WEEKLY_PROGRAM_ARRAY: [weeklyProgramSchema],
    LESSON: lessonSchema,
    LESSON_ARRAY: [lessonSchema],
    GRADE: gradeSchema,
    GRADE_ARRAY: [gradeSchema],
    TERM: termSchema,
    TERM_ARRAY: [termSchema],
    PAYMENT: paymentSchema,
    PAYMENT_ARRAY: [paymentSchema],
    ENROLMENT: enrolmentSchema,
    ENROLMENT_ARRAY: [enrolmentSchema],
    INDIVIDUAL_CONSULTATION: individualConsultationSchema,
    INDIVIDUAL_CONSULTATION_ARRAY: [individualConsultationSchema],
    INDIVIDUAL_CONSULTATION_DOCUMENT: individualConsultationDocumentSchema,
    INDIVIDUAL_CONSULTATION_DOCUMENT_ARRAY: [individualConsultationDocumentSchema],
    INDIVIDUAL_CONSULTATION_DASHBOARD_ARRAY: [individualConsultationDashboardSchema],
    CHILD_LESSON: childLessonSchema,
    CHILD_LESSON_ARRAY: [childLessonSchema],
    WEEKLY_INFO: weeklyInfoSchema,
    WEEKLY_INFO_ARRAY: [weeklyInfoSchema],
    SELF_ASSESSMENT: selfAssessmentSchema,
    SELF_ASSESSMENT_ARRAY: [selfAssessmentSchema],
    MEDICAL_INFORMATION: medicalInformationSchema,
    MEDICAL_INFORMATION_ARRAY: [medicalInformationSchema],
    WORKSHOP: workshopSchema,
    WORKSHOP_ARRAY: [workshopSchema],
    WORKSHOP_SESSION: workshopSessionSchema,
    WORKSHOP_SESSION_ARRAY: [workshopSessionSchema],
    WORKSHOP_ENROLMENT: workshopEnrolmentSchema,
    WORKSHOP_ENROLMENT_ARRAY: [workshopEnrolmentSchema],
    CHILD_WORKSHOP_SESSIONS_DASHBOARD_ARRAY: [workshopSessionsDashboardSchema],
    MAKEUP_SUMMARY_ARRAY: [makeupSummarySchema]
};

export const CALL_API = 'Call API';

// A Redux middleware that interprets actions with CALL_API info specified.
// Performs the call and promises when such actions are dispatched.
export default store => next => action => {
    const callAPI = action[CALL_API];
    if (typeof callAPI === 'undefined') {
        return next(action);
    }

    let { endpoint } = callAPI;
    const { types, method = 'GET', authorize = true, request, schema, form } = callAPI;

    const state = store.getState();

    if (typeof endpoint === 'function') {
        endpoint = endpoint(state);
    }

    if (typeof endpoint !== 'string') {
        throw new Error('Specify a string endpoint URL.');
    }
    if (!Array.isArray(types) || types.length !== 3) {
        throw new Error('Expected an array of three action types.');
    }
    if (!types.every(type => typeof type === 'string')) {
        throw new Error('Expected action types to be strings.');
    }

    const actionWith = data => {
        const finalAction = Object.assign({}, action, data);
        delete finalAction[CALL_API];
        return finalAction;
    };

    const [requestType, successType, failureType] = types;
    next(actionWith({ type: requestType, payload: request }));

    let body = undefined;
    if (method === 'GET') {
        const queryParams = encodeQueryParams(request);
        endpoint = `${endpoint}?${queryParams}`;
    } else {
        body = JSON.stringify(request || {}, (key, value) => {  // prevent error "411 - Content-Length header required" by passing empty object instead of null in the body
            if (key) {
                const initialValue = request[key];
                if (initialValue instanceof Date) {
                    // call format to include timezone info - "2015-09-16T00:00:00+01:00" (moment().toJSON() or default behaviour 
                    // of JSON.stringify returns UTC time)
                    return moment(initialValue).format();
                }
            }
            return value;
        });
    }

    const apiOptions = {
        method,
        endpoint,
        body,
        authorize
    };

    return callApi(apiOptions, schema, store.dispatch, store.getState).then(
        response => {
            const { transition, onSuccess } = callAPI;
            let { successPayload } = callAPI;

            if (typeof successPayload === 'function') {
                successPayload = successPayload(response);
            }

            next(actionWith({
                type: successType,
                response,
                ...successPayload
            }));

            if (transition) {
                const { success, history } = transition;
                if (success) {
                    redirect(success, response, history);
                }
            }

            if (onSuccess) {
                onSuccess(response);
            }
        },
        errorResponse => {
            const errors = processErrorResponse(errorResponse);

            const { errorPayload, transition, onError } = callAPI;

            next(actionWith({
                type: failureType,
                errorResponse,
                ...errorPayload
            }));

            if (form) {
                next(stopSubmit(form, { _error: errors.message, ...errors.validationErrors }));
            }

            if (transition) {
                let { error, history } = transition;
                if (error) {
                    redirect(error, errorResponse, history);
                }
            }

            if (onError) {
                onError(errorResponse);
            }
        }
    );
};

const redirect = ({ path, isExternal, push }, response, history) => {
    if (path) {
        if (typeof path === 'function') {
            path = path(response);
        }
        if (typeof path !== 'string') {
            throw new Error('Specify a string redirect URL.');
        }

        if (path) {
            if (isExternal) {
                window.location = path;
            } else {
                if (push) {
                    history.push(path);
                } else {
                    history.replace(path);
                }
            }
        }
    }
};